import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Sales, Tasks, BackOffice, Files, Masters, apiErrorMessage } from "../lib/api";
import { fmtDateTime } from "../lib/auditFormat";
import { getUploadPolicy, validateUpload } from "../lib/uploadPolicy";
import { TaskHoldControl, HoldDetailsCard } from "../components/TaskHoldControl";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, ShieldCheck, Receipt, Hash, Sparkles, Save, FileCheck,
  Upload, FileText, Eye, Trash2, AlertTriangle, CheckCircle2, Clock,
} from "lucide-react";
import { cn } from "../lib/utils";
import { buildDeliveryDateTime, getTimeRemaining, useTicker } from "../lib/time";
import { DelayMarker } from "../components/DelayMarker";
import { BillingLedgerBanner } from "../components/BillingLedgerBanner";

const TYPE_META = {
  insurance: { task: "INSURANCE", label: "Insurance", icon: ShieldCheck, fileType: "policy_doc" },
  "road-tax": { task: "ROAD_TAX", label: "Road Tax", icon: Receipt, fileType: "tax_receipt" },
  hsrp: { task: "HSRP", label: "HSRP", icon: Hash, fileType: "hsrp_receipt" },
  "vas-pp": { task: "VAS_PP", label: "Protection Plus", icon: Sparkles, fileType: null, vasKind: "pp" },
  "vas-amc": { task: "VAS_AMC", label: "AMC", icon: Sparkles, fileType: null, vasKind: "amc" },
  "vas-rsa": { task: "VAS_RSA", label: "RSA", icon: Sparkles, fileType: null, vasKind: "rsa" },
};

function TimerBanner({ deliveryAt }) {
  if (!deliveryAt) return null;
  const t = getTimeRemaining(deliveryAt);
  const tones = {
    success: "bg-emerald-50 text-emerald-800 border-emerald-200",
    warning: "bg-amber-50 text-amber-800 border-amber-200",
    danger: "bg-rose-50 text-rose-800 border-rose-200",
    neutral: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <span className={cn(
      "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 font-mono-data text-xs font-semibold",
      tones[t.tone],
    )}>
      <Clock className="h-3.5 w-3.5" />
      {t.overdue ? `Overdue ${t.label}` : `Time remaining ${t.label}`}
    </span>
  );
}

function ContextSection({ sale, loc }) {
  const row = (label, value) => (
    <div>
      <dt className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-slate-900">{value || <span className="text-slate-400">—</span>}</dd>
    </div>
  );
  return (
    <Card className="border-slate-200 p-5">
      <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">Sale Context · Read-only</div>
      <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
        {row("SR Number", <span className="font-mono-data">{sale.sr_number}</span>)}
        {row("Customer", sale.customer?.name)}
        {row("Mobile", sale.customer?.phone)}
        {row("Location", loc ? `${loc.name} (${loc.code})` : "—")}
        {row("Model", sale.vehicle?.model)}
        {row("VIN", <span className="font-mono-data">{sale.vehicle?.vin}</span>)}
        {row("Engine No.", <span className="font-mono-data">{sale.vehicle?.engine_no}</span>)}
        {row("Delivery Date", sale.delivery?.promised_date)}
      </dl>
    </Card>
  );
}

function FilesPanel({ saleId, fileType, label, readOnly, uploadedBy }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const refresh = useCallback(async () => {
    if (!fileType) return;
    setFiles(await Files.list(saleId, fileType));
  }, [saleId, fileType]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!fileType) return null;

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateUpload(fileType, file);
    if (err) { toast.error(err); e.target.value = ""; return; }
    setUploading(true);
    try {
      await Files.upload(saleId, fileType, file, uploadedBy);
      toast.success("Uploaded");
      await refresh();
    } catch (err) {
      toast.error(apiErrorMessage(err) || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const onRemove = async (fid) => {
    if (!window.confirm("Remove this file?")) return;
    try {
      await Files.remove(fid);
      await refresh();
    } catch (err) {
      toast.error(apiErrorMessage(err) || "Failed");
    }
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs font-semibold">{label}</Label>
      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4">
        {files.length === 0 ? (
          <div className="text-center"><FileText className="mx-auto h-7 w-7 text-slate-300" /><p className="mt-1 text-xs text-slate-500">No file uploaded yet</p></div>
        ) : (
          <ul className="space-y-2">
            {files.map((f) => (
              <li key={f.id} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="h-4 w-4 flex-shrink-0 text-slate-500" />
                  <span className="truncate text-sm text-slate-900">{f.original_filename || "file"}</span>
                  <span className="text-xs text-slate-400">({Math.round((f.size || 0) / 1024)} KB)</span>
                </div>
                <div className="flex items-center gap-1">
                  <a href={Files.url(f.id)} target="_blank" rel="noreferrer"
                    className="rounded-md border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-50"
                    data-testid={`bo-file-preview-${f.id}`}>
                    <Eye className="h-3.5 w-3.5" />
                  </a>
                  {!readOnly && (
                    <button onClick={() => onRemove(f.id)}
                      className="rounded-md border border-red-200 p-1.5 text-red-600 hover:bg-red-50"
                      data-testid={`bo-file-remove-${f.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        {!readOnly && (
          <div className="mt-3">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              <Upload className="h-4 w-4" />
              {files.length ? "Add another file" : "Upload file"}
              <input type="file" accept={getUploadPolicy(fileType).acceptAttr} onChange={onUpload}
                disabled={uploading} className="hidden"
                data-testid="bo-file-input" />
            </label>
            {uploading && <span className="ml-2 text-xs text-slate-500">Uploading…</span>}
          </div>
        )}
      </div>
    </div>
  );
}

export default function BackOfficeWorkspace() {
  const { taskType, saleId } = useParams();
  const navigate = useNavigate();
  const { activeUser, locations } = useApp();
  useTicker(60_000);

  const meta = TYPE_META[taskType];
  const [sale, setSale] = useState(null);
  const [task, setTask] = useState(null);
  const [tasksAll, setTasksAll] = useState([]);

  const load = useCallback(async () => {
    const s = await Sales.get(saleId);
    setSale(s);
    const ts = await Tasks.list({ sale_id: saleId, with_sla: "true" });
    setTasksAll(ts);
    setTask(ts.find((t) => t.type === meta?.task) || null);
  }, [saleId, meta]);

  useEffect(() => { load(); }, [load]);

  if (!meta) return <div className="py-10 text-center text-sm text-slate-500">Unknown task type.</div>;
  if (!sale) return <div className="py-10 text-center text-sm text-slate-500">Loading…</div>;

  const loc = locations.find((l) => l.id === sale.location_id);
  const isCompleted = task?.status === "completed" || task?.status === "skipped";
  const isOwner = activeUser?.role === "BackOffice" || activeUser?.role === "Admin";
  const isSm = activeUser?.role === "SalesManager";
  // Actions + form inputs are locked for anyone not the owner (or once the
  // task is done). Uploads have a more permissive gate — Sales Managers can
  // still attach documents on behalf of the branch.
  const readOnly = !isOwner || isCompleted;
  const uploadReadOnly = (!isOwner && !isSm) || isCompleted;
  const deliveryAt = buildDeliveryDateTime(sale.delivery?.promised_date, sale.delivery?.promised_time);

  // Dependency gating banner
  const taskByType = Object.fromEntries(tasksAll.map((t) => [t.type, t]));
  const dependencyBlocked = (() => {
    if (meta.task === "ROAD_TAX") {
      const ins = taskByType.INSURANCE;
      if (!ins || ins.status === "pending") return "Insurance must be marked Done or Customer Not Opted first.";
    }
    if (meta.task === "HSRP") {
      const rt = taskByType.ROAD_TAX;
      if (!rt || rt.status !== "completed") return "Road Tax must be completed before HSRP.";
    }
    return null;
  })();

  const Header = (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <button onClick={() => navigate("/back-office")}
          className="mb-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"
          data-testid="bo-back-btn">
          <ArrowLeft className="h-3 w-3" /> Back to Queue
        </button>
        <div className="flex items-center gap-3">
          <h1 className="font-display text-3xl font-semibold tracking-tight">
            {meta.label}
          </h1>
          {isCompleted && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
              <CheckCircle2 className="h-3.5 w-3.5" />
              {task.status === "skipped" ? "Skipped" : "Completed"}
            </span>
          )}
        </div>
        <div className="mt-1 text-sm text-slate-500">
          <Link to={`/sales/${sale.id}`} className="underline hover:text-primary">
            Open full sale detail
          </Link>
        </div>
      </div>
      <TimerBanner deliveryAt={deliveryAt} />
    </div>
  );

  const Common = (
    <>
      {Header}
      <ContextSection sale={sale} loc={loc} />
      <BillingLedgerBanner sale={sale} onUpdated={load} />
      {dependencyBlocked && !isCompleted && (
        <Card className="border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5" />
            <span>{dependencyBlocked}</span>
          </div>
        </Card>
      )}
    </>
  );

  return (
    <div className="space-y-5" data-testid="bo-workspace-page">
      {meta.task === "INSURANCE" && <InsuranceForm common={Common} sale={sale} task={task} readOnly={readOnly} uploadReadOnly={uploadReadOnly}
        userId={activeUser?.id} reload={load} fileType={meta.fileType} dependencyBlocked={dependencyBlocked} />}
      {meta.task === "ROAD_TAX" && <RoadTaxForm common={Common} sale={sale} task={task} readOnly={readOnly} uploadReadOnly={uploadReadOnly}
        userId={activeUser?.id} reload={load} fileType={meta.fileType} dependencyBlocked={dependencyBlocked} />}
      {meta.task === "HSRP" && <HsrpForm common={Common} sale={sale} task={task} readOnly={readOnly} uploadReadOnly={uploadReadOnly}
        userId={activeUser?.id} reload={load} fileType={meta.fileType} dependencyBlocked={dependencyBlocked} />}
      {meta.task?.startsWith("VAS_") && <VasItemForm common={Common} sale={sale} readOnly={readOnly} uploadReadOnly={uploadReadOnly}
        userId={activeUser?.id} reload={load} kind={meta.vasKind} label={meta.label} />}
    </div>
  );
}

// ───────────────────── INSURANCE ─────────────────────
function InsuranceForm({ common, sale, task, readOnly, uploadReadOnly, userId, reload, fileType, dependencyBlocked }) {
  const [status, setStatus] = useState(sale.insurance?.status || "");
  const [company, setCompany] = useState(sale.insurance?.company || "");
  const [policy, setPolicy] = useState(sale.insurance?.policy_number || "");
  const [planType, setPlanType] = useState(sale.insurance?.plan_type || "");
  const [notes, setNotes] = useState(sale.insurance?.notes || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setStatus(sale.insurance?.status || "");
    setCompany(sale.insurance?.company || "");
    setPolicy(sale.insurance?.policy_number || "");
    setPlanType(sale.insurance?.plan_type || "");
    setNotes(sale.insurance?.notes || "");
  }, [sale]);

  const canComplete = status === "not_opted"
    || (status === "done" && company.trim() && policy.trim() && planType);

  const onComplete = async () => {
    setBusy(true);
    try {
      await BackOffice.insuranceComplete(sale.id, {
        status,
        company: status === "done" ? company : null,
        policy_number: status === "done" ? policy : null,
        plan_type: status === "done" ? planType : null,
        notes: notes || null,
        user_id: userId,
      });
      toast.success(status === "not_opted" ? "Marked Customer Not Opted" : "Insurance completed", { duration: 2500 });
      reload();
    } catch (err) {
      toast.error(apiErrorMessage(err) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      {common}
      <HoldDetailsCard task={task} />
      <Card className="border-slate-200 p-5 sm:p-6" data-testid="bo-insurance-section">
        <div className="flex items-start justify-between gap-3">
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">Insurance Details</div>
          {!task?.delay_marked && (
            <DelayMarker task={task} userId={userId} onChange={reload} readOnly={readOnly}
              testIdPrefix="insurance-delay" />
          )}
        </div>
        {task?.delay_marked && (
          <div className="mt-3">
            <DelayMarker task={task} userId={userId} onChange={reload} readOnly={readOnly}
              testIdPrefix="insurance-delay" />
          </div>
        )}
        <div className="mt-4 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Insurance Status *</Label>
            <Select value={status} onValueChange={setStatus} disabled={readOnly || dependencyBlocked}>
              <SelectTrigger className="w-full sm:w-[300px]" data-testid="insurance-status">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="done">Done — Policy Issued</SelectItem>
                <SelectItem value="not_opted">Customer Not Opted</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {status === "done" && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Plan Type *</Label>
                <Select value={planType} onValueChange={setPlanType} disabled={readOnly}>
                  <SelectTrigger data-testid="insurance-plan-type">
                    <SelectValue placeholder="Select plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1+5">1 OD + 5 TP</SelectItem>
                    <SelectItem value="5+5">5 OD + 5 TP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Insurance Company *</Label>
                <Input value={company} onChange={(e) => setCompany(e.target.value)}
                  disabled={readOnly} placeholder="e.g. ICICI Lombard" data-testid="insurance-company" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Policy Number *</Label>
                <Input value={policy} onChange={(e) => setPolicy(e.target.value)}
                  disabled={readOnly} placeholder="e.g. PL-2026-00123" data-testid="insurance-policy" />
              </div>
            </div>
          )}
          {status === "done" && (
            <FilesPanel saleId={sale.id} fileType={fileType} label="Upload Policy Document"
              readOnly={uploadReadOnly ?? readOnly} uploadedBy={userId} />
          )}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              disabled={readOnly} data-testid="insurance-notes" />
          </div>
        </div>
      </Card>
      {!readOnly && (
        <div className="flex items-center justify-end gap-2">
          <TaskHoldControl task={task} userId={userId} onChanged={reload} />
          <Button onClick={onComplete} disabled={!canComplete || busy || !!dependencyBlocked || task?.sla_paused}
            className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:hover:bg-slate-300"
            data-testid="insurance-complete-btn">
            <FileCheck className="h-4 w-4" />
            {busy ? "Saving…" : (status === "not_opted" ? "Mark Not Opted & Continue" : "Mark Insurance Done")}
          </Button>
        </div>
      )}
    </>
  );
}

// ───────────────────── ROAD TAX ─────────────────────
const RTO_STAGE_META = {
  pending: {
    label: "Pending for RTO Processing",
    sub: "Awaiting submission to RTO",
    pillCls: "bg-slate-100 text-slate-700 ring-slate-200",
    cardCls: "border-slate-200",
  },
  processing: {
    label: "RTO Processing",
    sub: "Submitted to RTO · Awaiting approval",
    pillCls: "bg-blue-50 text-blue-700 ring-blue-200",
    cardCls: "border-blue-200",
  },
  completed: {
    label: "Registration Completed",
    sub: "Registration completed",
    pillCls: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    cardCls: "border-emerald-200",
  },
};

function RoadTaxForm({ common, sale, task, readOnly, uploadReadOnly, userId, reload, fileType, dependencyBlocked }) {
  const [receipt, setReceipt] = useState(sale.road_tax?.receipt_no || "");
  const [regn, setRegn] = useState(sale.road_tax?.regn_no || "");
  const [notes, setNotes] = useState(sale.road_tax?.notes || "");
  const [busy, setBusy] = useState(false);
  const [toggling, setToggling] = useState(false);

  const submittedToRto = !!sale.road_tax?.submitted_to_rto;
  const completed = task?.status === "completed";
  const rtoStage = completed
    ? "completed"
    : submittedToRto ? "processing" : "pending";
  const stageMeta = RTO_STAGE_META[rtoStage];
  // Fields are locked until "Submitted to RTO?" = Yes
  const fieldsLocked = !submittedToRto || readOnly || dependencyBlocked;

  useEffect(() => {
    setReceipt(sale.road_tax?.receipt_no || "");
    setRegn(sale.road_tax?.regn_no || "");
    setNotes(sale.road_tax?.notes || "");
  }, [sale]);

  const canComplete = submittedToRto && receipt.trim() && regn.trim();

  const onToggleRto = async (next) => {
    if (toggling || readOnly || dependencyBlocked) return;
    setToggling(true);
    try {
      await BackOffice.roadTaxSubmitToRto(sale.id, { submitted: next, user_id: userId });
      toast.success(next ? "Submitted to RTO · timer started" : "Submission reverted");
      reload();
    } catch (err) {
      toast.error(apiErrorMessage(err) || "Failed");
    } finally { setToggling(false); }
  };

  const onComplete = async () => {
    setBusy(true);
    try {
      await BackOffice.roadTaxComplete(sale.id, {
        receipt_no: receipt, regn_no: regn,
        notes: notes || null, user_id: userId,
      });
      toast.success("Road Tax completed · HSRP unlocked", { duration: 2500 });
      reload();
    } catch (err) {
      toast.error(apiErrorMessage(err) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      {common}
      <HoldDetailsCard task={task} />

      {/* RTO Processing Status — single Yes/No toggle that drives everything */}
      <Card className={`border p-5 sm:p-6 ${stageMeta.cardCls}`} data-testid="bo-roadtax-rto-status">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
              RTO Processing Status
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ${stageMeta.pillCls}`}
                data-testid="rto-stage-pill"
              >
                {stageMeta.label}
              </span>
              {completed && (sale.road_tax?.regn_no || regn) && (
                <span className="font-mono-data text-xs text-slate-600">
                  · {sale.road_tax?.regn_no || regn}
                </span>
              )}
            </div>
            <div className="mt-1 text-xs text-slate-500">{stageMeta.sub}</div>
          </div>
          {!completed && (
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold text-slate-600">Submitted to RTO?</span>
              <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
                <button
                  type="button"
                  disabled={toggling || readOnly || dependencyBlocked}
                  onClick={() => onToggleRto(false)}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                    !submittedToRto
                      ? "bg-slate-700 text-white"
                      : "bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                  data-testid="rto-toggle-no"
                >
                  No
                </button>
                <button
                  type="button"
                  disabled={toggling || readOnly || dependencyBlocked}
                  onClick={() => onToggleRto(true)}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                    submittedToRto
                      ? "bg-emerald-600 text-white"
                      : "bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                  data-testid="rto-toggle-yes"
                >
                  Yes
                </button>
              </div>
            </div>
          )}
        </div>
        {!submittedToRto && !completed && (
          <div className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-amber-200">
            Fields will activate after submission to RTO.
          </div>
        )}
        {submittedToRto && !completed && sale.road_tax?.submitted_to_rto_at && (
          <div className="mt-3 text-[11px] text-slate-500">
            Submitted on{" "}
            <span className="font-mono-data text-slate-700">
              {fmtDateTime(sale.road_tax.submitted_to_rto_at)}
            </span>
          </div>
        )}
      </Card>

      <Card className="border-slate-200 p-5 sm:p-6" data-testid="bo-roadtax-section">
        <div className="flex items-start justify-between gap-3">
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">Road Tax · Registration</div>
          {!task?.delay_marked && (
            <DelayMarker task={task} userId={userId} onChange={reload} readOnly={readOnly}
              testIdPrefix="roadtax-delay" />
          )}
        </div>
        {task?.delay_marked && (
          <div className="mt-3">
            <DelayMarker task={task} userId={userId} onChange={reload} readOnly={readOnly}
              testIdPrefix="roadtax-delay" />
          </div>
        )}
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Tax Receipt Number *</Label>
            <Input value={receipt} onChange={(e) => setReceipt(e.target.value)}
              disabled={fieldsLocked} placeholder="e.g. RT-OD-26-001"
              className={fieldsLocked ? "opacity-60" : ""}
              data-testid="roadtax-receipt" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Registration Number *</Label>
            <Input value={regn} onChange={(e) => setRegn(e.target.value)}
              disabled={fieldsLocked} placeholder="e.g. OD05AB1234"
              className={fieldsLocked ? "opacity-60" : ""}
              data-testid="roadtax-regn" />
          </div>
        </div>
        <div className={`mt-4 ${(uploadReadOnly ?? fieldsLocked) ? "pointer-events-none opacity-60" : ""}`}>
          <FilesPanel saleId={sale.id} fileType={fileType} label="Upload Tax Receipt"
            readOnly={uploadReadOnly ?? fieldsLocked} uploadedBy={userId} />
        </div>
        <div className="mt-4 space-y-1.5">
          <Label className="text-xs font-semibold">Notes</Label>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
            disabled={fieldsLocked} className={fieldsLocked ? "opacity-60" : ""}
            data-testid="roadtax-notes" />
        </div>
      </Card>
      {!readOnly && !completed && (
        <div className="flex items-center justify-end gap-2">
          <TaskHoldControl task={task} userId={userId} onChanged={reload} />
          <Button onClick={onComplete}
            disabled={!canComplete || busy || !!dependencyBlocked || !submittedToRto || task?.sla_paused}
            className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:hover:bg-slate-300"
            data-testid="roadtax-complete-btn">
            <FileCheck className="h-4 w-4" />
            {busy ? "Saving…"
              : !submittedToRto ? "Mark Road Tax Completed (locked — submit to RTO first)"
              : "Mark Road Tax Completed"}
          </Button>
        </div>
      )}
    </>
  );
}

// ───────────────────── HSRP ─────────────────────
function HsrpForm({ common, sale, task, readOnly, uploadReadOnly, userId, reload, fileType, dependencyBlocked }) {
  const [orderStatus, setOrderStatus] = useState(sale.hsrp?.order_status || "");
  const [fitment, setFitment] = useState(sale.hsrp?.fitment_status || "pending");
  const [notes, setNotes] = useState(sale.hsrp?.notes || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setOrderStatus(sale.hsrp?.order_status || "");
    setFitment(sale.hsrp?.fitment_status || "pending");
    setNotes(sale.hsrp?.notes || "");
  }, [sale]);

  const onSave = async (action) => {
    setBusy(true);
    try {
      const payload = {
        order_status: orderStatus || null,
        fitment_status: fitment,
        notes: notes || null,
        user_id: userId,
      };
      if (action === "complete") payload.fitment_status = "completed";
      await BackOffice.hsrpUpdate(sale.id, payload);
      toast.success(action === "complete"
        ? "HSRP fitted · task completed"
        : "HSRP saved");
      reload();
    } catch (err) {
      toast.error(apiErrorMessage(err) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      {common}
      <HoldDetailsCard task={task} />
      <Card className="border-slate-200 p-5 sm:p-6" data-testid="bo-hsrp-section">
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">HSRP Tracking</div>
        <p className="mt-2 text-xs text-slate-500">
          Initiating the HSRP order is enough to unlock delivery. Mark fitment completed once the plate is fixed.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">HSRP Order Status</Label>
            <Select value={orderStatus} onValueChange={setOrderStatus} disabled={readOnly || dependencyBlocked}>
              <SelectTrigger data-testid="hsrp-order-status"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ordered">Ordered</SelectItem>
                <SelectItem value="not_ordered">Not Ordered</SelectItem>
              </SelectContent>
            </Select>
            {sale.hsrp?.ordered_at && (
              <p className="text-[11px] text-slate-500">Ordered on {fmtDateTime(sale.hsrp.ordered_at)}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Fitment Status</Label>
            <Select value={fitment} onValueChange={setFitment} disabled={readOnly || dependencyBlocked}>
              <SelectTrigger data-testid="hsrp-fitment-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4">
          <FilesPanel saleId={sale.id} fileType={fileType} label="HSRP Receipt / Slip"
            readOnly={(uploadReadOnly ?? readOnly) || dependencyBlocked} uploadedBy={userId} />
        </div>
        <div className="mt-4 space-y-1.5">
          <Label className="text-xs font-semibold">Notes</Label>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
            disabled={readOnly || dependencyBlocked} data-testid="hsrp-notes" />
        </div>
      </Card>
      {!readOnly && (
        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
          <Button variant="outline" onClick={() => onSave("save")}
            disabled={busy || !!dependencyBlocked} className="gap-1.5"
            data-testid="hsrp-save-btn">
            <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save Progress"}
          </Button>
          <TaskHoldControl task={task} userId={userId} onChanged={reload} />
          <Button onClick={() => onSave("complete")}
            disabled={busy || !!dependencyBlocked || task?.sla_paused}
            className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:hover:bg-slate-300"
            data-testid="hsrp-complete-btn">
            <FileCheck className="h-4 w-4" /> Mark Fitment Completed
          </Button>
        </div>
      )}
    </>
  );
}

// ───────────────────── VAS · per-plan ─────────────────────
function VasItemForm({ common, sale, readOnly, uploadReadOnly, userId, reload, kind, label }) {
  const initial = sale.vas_status?.[kind] || {};
  const [status, setStatus] = useState(initial.status || "");
  const [remarks, setRemarks] = useState(initial.remarks || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const v = sale.vas_status?.[kind] || {};
    setStatus(v.status || "");
    setRemarks(v.remarks || "");
  }, [sale, kind]);

  const onAction = async (action) => {
    if (action === "complete" && !status) {
      toast.error("Pick Done or Customer Not Opted to complete");
      return;
    }
    setBusy(true);
    try {
      const payload = { status: status || null, remarks: remarks || null, user_id: userId };
      if (action === "complete") {
        await BackOffice.vasComplete(sale.id, kind, payload);
        toast.success(status === "not_opted" ? "Marked Customer Not Opted" : `${label} completed`);
      } else {
        await BackOffice.vasSaveDraft(sale.id, kind, payload);
        toast.success("Saved");
      }
      reload();
    } catch (err) {
      toast.error(apiErrorMessage(err) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      {common}
      <Card className="border-slate-200 p-5 sm:p-6" data-testid="bo-vas-section">
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">{label}</div>
        <p className="mt-2 text-xs text-slate-500">
          Independent of the registration chain. Completing this does not block delivery.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Status</Label>
            <Select value={status} onValueChange={setStatus} disabled={readOnly}>
              <SelectTrigger data-testid="vas-status">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="done">Done — Service Activated</SelectItem>
                <SelectItem value="not_opted">Customer Not Opted</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4 space-y-1.5">
          <Label className="text-xs font-semibold">Remarks</Label>
          <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)}
            disabled={readOnly} placeholder="Optional" data-testid="vas-remarks" />
        </div>
      </Card>
      {kind === "pp" && (
        <Card className="border-slate-200 p-5 sm:p-6" data-testid="bo-pp-docs-section">
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
            Protection Plus · Documents
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Upload the PP certificate issued by the underwriter and the corresponding tax invoice.
            Both will be picked up automatically in the customer’s Delivery File.
          </p>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FilesPanel
              saleId={sale.id}
              fileType="vas_pp_certificate"
              label="PP Certificate"
              readOnly={readOnly}
              uploadedBy={userId}
            />
            <FilesPanel
              saleId={sale.id}
              fileType="vas_pp_invoice"
              label="PP Invoice"
              readOnly={readOnly}
              uploadedBy={userId}
            />
          </div>
        </Card>
      )}
      {kind === "amc" && (
        <Card className="border-slate-200 p-5 sm:p-6" data-testid="bo-amc-docs-section">
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
            AMC · Documents
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Upload the AMC certificate and tax invoice. Both flow into the Delivery File automatically.
          </p>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FilesPanel
              saleId={sale.id}
              fileType="vas_amc_certificate"
              label="AMC Certificate"
              readOnly={readOnly}
              uploadedBy={userId}
            />
            <FilesPanel
              saleId={sale.id}
              fileType="vas_amc_invoice"
              label="AMC Invoice"
              readOnly={readOnly}
              uploadedBy={userId}
            />
          </div>
        </Card>
      )}
      {kind === "rsa" && (
        <Card className="border-slate-200 p-5 sm:p-6" data-testid="bo-rsa-docs-section">
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
            RSA · Policy
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Upload the RSA policy document. It will be picked up automatically in the Delivery File.
          </p>
          <div className="mt-4 sm:max-w-md">
            <FilesPanel
              saleId={sale.id}
              fileType="vas_rsa_policy"
              label="RSA Policy"
              readOnly={readOnly}
              uploadedBy={userId}
            />
          </div>
        </Card>
      )}
      {!readOnly && (
        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
          <Button variant="outline" onClick={() => onAction("save")} disabled={busy} className="gap-1.5"
            data-testid="vas-save-btn">
            <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save Draft"}
          </Button>
          <Button onClick={() => onAction("complete")} disabled={busy || !status}
            className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:hover:bg-slate-300"
            data-testid="vas-complete-btn">
            <FileCheck className="h-4 w-4" />
            {status === "not_opted" ? "Mark Not Opted" : `Mark ${label} Done`}
          </Button>
        </div>
      )}
    </>
  );
}
